<template>
  <div class="recruitment-show">
    <page-header title="RECRUITMENT">
      <template #action>
        <v-btn
          :to="{ name: 'project-show', params: { id: projectId } }"
          link
          color="button"
          class="fw-400"
        >
          BACK
        </v-btn>
      </template>
      <template #breadcrumbs>
        <router-link :to="{ name: 'projects' }">
          Projects
        </router-link>
        <span>/</span>
        <router-link :to="{ name: 'project-show', params: { id: projectId } }">
          {{ recruitmentData.project_name }}
        </router-link>
        <span>/ Recruitment - {{ recruitmentData.position }}</span>
      </template>
    </page-header>

    <div class="recruitment-show__content">
      <RecruitmentDetailsForm
        :recruitment-base="recruitmentData"
        :submit-name="submitName"
        @submit="update"
      />

       <v-row>
        <v-col cols="12" lg="6" class="pt-0">
          <ProjectNotesCard :notes="recruitmentData.notes || ''"
            @edit="openNotesModal"
          />
        </v-col>

        <v-col cols="12" lg="6" class="pt-0">
          <AttachmentsCard
            :attachments="recruitmentData.attachments"
            @edit="openFileModal"
            @delete="deleteAttachment"
          />
        </v-col>
      </v-row>

      <SectionHeader
        title="CANDIDATES"
        :subtitle="`${recruitmentData.candidates.length} CANDIDATES`"
      />

      <div class="recruitment-show__candidates">
        <v-row v-if="recruitmentData.candidates.length">
          <v-col
            v-for="candidate in recruitmentData.candidates"
            :key="candidate.id"
            class="col-12 col-lg-6"
          >
            <candidate-card
              :candidate="candidate"
              :menu="isMenuVisible(candidate.status)"
              dev-route="developer-show"
            >
              <template #menu>
                <v-list dense elevation-1>
                  <v-list-item
                    v-if="candidate.status === 'In verification'"
                    @click="changeStatus(candidate.id, 'Accepted')"
                  >
                    <v-list-item-title>Accept</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    v-if="candidate.status === 'Offered'"
                    @click="changeStatus(candidate.id, 'In verification')"
                  >
                    <v-list-item-title>Verify</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    v-if="candidate.status === 'Offered' || candidate.status === 'In verification'"
                    @click="changeStatus(candidate.id, 'Rejected')"
                  >
                    <v-list-item-title>Reject</v-list-item-title>
                  </v-list-item>
                </v-list>
              </template>
            </candidate-card>
          </v-col>
        </v-row>

        <v-row v-else>
          <v-col class="my-6">
            <NoData
              title="Your recruitment list is empty"
              description="You will receive a list of matched developers soon"
            />
          </v-col>
        </v-row>
      </div>

    </div>

    <EditTextModal
      v-model="isNotesModalVisible"
      :description="recruitmentData.notes || ''"
      @add="addNotes"
    />

    <AttachmentsModal
      v-model="isFileModalVisible"
      :attachments="recruitmentData.attachments"
      :isSubmitProcessing="isAttachmentProcessing"
      @addAttachments="addAttachments"
    />

    <ConfirmDialog ref="confirmDialog" />
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import PageHeader from '@/components/PageHeader.vue';
import NoData from '@/components/NoData.vue';
import SectionHeader from '@/components/SectionHeader.vue';
import RecruitmentDetailsForm from '@/components/Recruitments/RecruitmentDetailsForm.vue';
import CandidateCard from '@/components/CandidateCard.vue';
import ConfirmDialog from '@/components/ConfirmDialog.vue';
import ProjectNotesCard from '@/components/ProjectNotesCard.vue';
import EditTextModal from '@/components/EditTextModal.vue';
import AttachmentsCard from '@/components/AttachmentsCard.vue';
import AttachmentsModal from '@/components/AttachmentsModal.vue';

export default {
  components: {
    PageHeader,
    NoData,
    SectionHeader,
    RecruitmentDetailsForm,
    CandidateCard,
    ConfirmDialog,
    ProjectNotesCard,
    AttachmentsCard,
    EditTextModal,
    AttachmentsModal,
  },

  props: {
    projectId: {
      type: [String, Number],
      required: true,
    },
    recruitmentId: {
      type: [String, Number],
      required: true,
    },
  },

  data() {
    return {
      submitName: 'Save Recruitment',
      isNotesModalVisible: false,
      isFileModalVisible: false,
    };
  },

  computed: {
    ...mapState('recruitment', [
      'recruitmentData',
      'isAttachmentSubmitProcessing',
    ]),

    ...mapState('attachment', ['isAttachmentProcessing']),
  },

  created() {
    this.fetchRecruitment(this.recruitmentId);
  },

  methods: {
    ...mapActions('recruitment', [
      'fetchRecruitment',
      'updateRecruitment',
      'updateCandidateStatus',
      'addRecruitmentNotes',
    ]),

    ...mapActions('attachment', [
      'uploadAttachments',
      'deleteAttachments',
    ]),

    update(recruitment) {
      this.updateRecruitment(recruitment);
    },

    changeStatus(candidateId, candidateStatus) {
      this.$refs.confirmDialog
        .open({
          message: 'Are you sure?',
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
        })
        .then((confirm) => {
          if (confirm) {
            const candidateStatusData = {
              id: candidateId,
              status: candidateStatus,
            };
            this.updateCandidateStatus(candidateStatusData)
              .then(() => {
                this.fetchRecruitment(this.recruitmentId);
              });
          }
        });
    },

    addNotes(notes) {
      this.addRecruitmentNotes(notes);
      this.updateRecruitment(this.recruitmentData);
    },

    addAttachments(files) {
      const attachmentData = {
        attachments: files,
        apiType: 'client',
        id: this.recruitmentId,
        recordType: 'recruitment',
      };

      this.uploadAttachments(attachmentData)
        .then(() => {
          this.fetchRecruitment(this.recruitmentId);
          this.isFileModalVisible = false;
        });
    },

    deleteAttachment(attachmentId) {
      const attachmentData = {
        id: attachmentId,
        apiType: 'client',
      };

      this.$refs.confirmDialog
        .open({
          message: 'Are you sure you want to delete?',
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
        })
        .then((confirm) => {
          if (confirm) {
            this.deleteAttachments(attachmentData)
              .then(() => {
                this.fetchRecruitment(this.recruitmentId);
              });
          }
        });
    },

    isMenuVisible(status) {
      return !['Accepted', 'Rejected', 'Contracted', 'Draft'].includes(status);
    },

    openNotesModal() {
      this.isNotesModalVisible = true;
    },

    openFileModal() {
      this.isFileModalVisible = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.recruitment-show {
  &__candidates {
    margin-bottom: 16px;
  }
}
</style>
